import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { FONTS } from '../../constants/config';

// ============================================================
// Types
// ============================================================

export interface LegendItem {
  label: string;
  color: string;
  value?: string | number;
  type?: 'dot' | 'square' | 'line' | 'dashed';
}

interface ChartLegendProps {
  items: LegendItem[];
  columns?: 2 | 3 | 4 | 'auto';
  showBorder?: boolean;
  compact?: boolean;
}

// ============================================================
// ChartLegend Component
// ============================================================

export const ChartLegend = ({
  items,
  columns = 'auto',
  showBorder = true,
  compact = false,
}: ChartLegendProps) => {
  const { colors, isDark } = useTheme();

  const renderLegendIndicator = (item: LegendItem) => {
    const type = item.type || 'dot';

    switch (type) {
      case 'square':
        return (
          <View
            style={[
              styles.legendSquare,
              { backgroundColor: item.color },
            ]}
          />
        );
      case 'line':
        return (
          <View
            style={[
              styles.legendLine,
              { backgroundColor: item.color },
            ]}
          />
        );
      case 'dashed':
        return (
          <View
            style={[
              styles.legendLineDashed,
              { borderColor: item.color },
            ]}
          />
        );
      case 'dot':
      default:
        return (
          <View
            style={[
              styles.legendDot,
              { backgroundColor: item.color },
            ]}
          />
        );
    }
  };

  return (
    <View
      style={[
        styles.container,
        showBorder && {
          borderTopWidth: 1,
          borderTopColor: colors.cardBorder,
        },
        compact && styles.containerCompact,
      ]}
    >
      {items.map((item, index) => (
        <View key={index} style={[styles.legendItem, compact && styles.legendItemCompact]}>
          {renderLegendIndicator(item)}
          <Text
            style={[
              styles.legendLabel,
              { color: colors.textSecondary },
              compact && styles.legendLabelCompact,
            ]}
          >
            {item.label}
          </Text>
          {item.value !== undefined && (
            <Text
              style={[
                styles.legendValue,
                { color: colors.textPrimary },
                compact && styles.legendValueCompact,
              ]}
            >
              {typeof item.value === 'number' ? `%${item.value}` : item.value}
            </Text>
          )}
        </View>
      ))}
    </View>
  );
};

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 16,
    paddingTop: 16,
    paddingHorizontal: 8,
  },
  containerCompact: {
    gap: 12,
    paddingTop: 12,
  },
  legendItem: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 6,
  },
  legendItemCompact: {
    gap: 4,
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  legendSquare: {
    width: 10,
    height: 10,
    borderRadius: 2,
  },
  legendLine: {
    width: 20,
    height: 3,
    borderRadius: 2,
  },
  legendLineDashed: {
    width: 20,
    height: 0,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderRadius: 2,
  },
  legendLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
  },
  legendLabelCompact: {
    fontSize: 11,
  },
  legendValue: {
    fontSize: 12,
    fontFamily: FONTS.medium,
  },
  legendValueCompact: {
    fontSize: 11,
  },
});

export default ChartLegend;
